import PropTypes from 'prop-types';
import React, { useCallback, useContext } from 'react';
import styled from 'styled-components';

import ChannelsAdmin from 'models/location/channels_admin';
import connect from 'components/lib/connect';
import { getLocationUrl } from 'scripts/adapters/routes/location_url';
import { HotkeysShownContext } from 'components/contexts/hotkeys';
import Inbox from 'models/location/inbox';
import InboxesAdmin from 'models/location/inboxes_admin';
import Integrations from 'models/location/integrations';
import KbAdmin from 'models/location/kb_admin';
import Link from 'components/common/link';
import Liveboards from 'models/location/liveboards';
import NavigateTo from 'actions/current_location/navigate_to';
import Reports from 'models/location/reports';
import track from 'scripts/presentation/components/lib/track';
import { useExecuteAction } from 'components/hooks/connect_hooks';
import { useIsFeatureEnabled } from 'components/contexts/is_feature_enabled';
import UsersAdmin from 'models/location/users_admin';

export const SettingsMenuHeader = styled.div`
  color: ${p => p.theme.colors.medium};
  font-size: ${p => p.theme.fontSize.base};
  font-weight: ${p => p.theme.fontWeight.medium};
  padding: ${p => p.theme.spacing.medium} 0px;
`;
export const StyledLink = styled(Link)`
  color: ${p => p.theme.colors.black};
  cursor: pointer;
  &:hover {
    color: ${p => p.theme.colors.green400};
    text-decoration: none;
  }
`;
export const StyledExternalLink = styled.a`
  color: ${p => p.theme.colors.black};
  cursor: pointer;
  &:hover {
    color: ${p => p.theme.colors.green400};
    text-decoration: none;
  }
`;

export default function NavigationMenu({ onClose }) {
  const isFeatureEnabled = useIsFeatureEnabled();
  const { dispatchHotkeysShown } = useContext(HotkeysShownContext);
  const showHotkeys = useCallback(() => dispatchHotkeysShown('on'), [dispatchHotkeysShown]);

  return (
    <div className="navigationMenu">
      <div className="navigationMenu-top">
        {isFeatureEnabled('internalAgentActions') ? (
          <MenuOption title="My Inbox" locationModel={Inbox} onClick={track.click('navigationMenu-myInbox', onClose)} />
        ) : null}
        {isFeatureEnabled('kbManagement') || isFeatureEnabled('internalAgentActions') ? (
          <MenuOption title="Answers" locationModel={KbAdmin} onClick={onClose} />
        ) : null}
        {isFeatureEnabled('viewReports') ? (
          <MenuOption title="Reports" locationModel={Reports} onClick={onClose} />
        ) : null}
        {isFeatureEnabled('viewLiveboards') ? (
          <MenuOption title="Liveboards" locationModel={Liveboards} onClick={onClose} />
        ) : null}
      </div>
      <SettingsMenu onClose={onClose} />
      <div className="navigationMenu-bottom">
        <div className="navigationMenu-item">
          <StyledExternalLink data-aid="navigationMenu-help-link" href="/support.html" target="help" tabIndex="-1">
            Help
          </StyledExternalLink>
        </div>
        <div className="navigationMenu-item">
          <StyledLink data-aid="navigationMenu-shortcuts-link" onClick={showHotkeys} tabIndex="-1">
            Keyboard Shortcuts
          </StyledLink>
        </div>
      </div>
    </div>
  );
}

NavigationMenu.propTypes = {
  onClose: PropTypes.func.isRequired,
};

function MenuOptionBase({ locationModel, onClick, title }) {
  const executeAction = useExecuteAction();
  const handleClick = useCallback(
    evt => {
      evt.preventDefault();
      executeAction(NavigateTo, locationModel.create());
      onClick();
    },
    [executeAction, locationModel, onClick]
  );

  return (
    <div className="navigationMenu-item">
      <StyledLink className="navigationMenu-option" href={getLocationUrl(locationModel.create())} onClick={handleClick}>
        {title}
      </StyledLink>
    </div>
  );
}

MenuOptionBase.propTypes = {
  locationModel: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
};

const MenuOption = React.memo(MenuOptionBase);
export { MenuOption };

const SETTINGS_OPTIONS = Object.freeze([
  { label: 'Integrations', locationModel: Integrations, feature: 'integrationsManagement' },
  { label: 'Users', locationModel: UsersAdmin, feature: 'teamManagement' },
  { label: 'Inboxes', locationModel: InboxesAdmin, feature: 'inboxManagement' },
  { label: 'Channels', locationModel: ChannelsAdmin, feature: 'channelManagement' },
]);

function SettingsMenuBase({ hasLookupConnector, onClose }) {
  const isFeatureEnabled = useIsFeatureEnabled();

  let availableOptions = SETTINGS_OPTIONS.filter(item => isOptionEnabled(item));

  if (availableOptions.length || isFeatureEnabled('kbManagement')) {
    return (
      <div className="navigationMenu-bottom">
        <SettingsMenuHeader>Settings</SettingsMenuHeader>
        {availableOptions.map(item => (
          <MenuOption key={item.label} title={item.label} locationModel={item.locationModel} onClick={onClose} />
        ))}
        {isFeatureEnabled('adminSettings') && <AdminSettingsLink />}
      </div>
    );
  }

  return null;

  function isOptionEnabled(item) {
    switch (item.feature) {
      case 'integrationsManagement':
        return (isFeatureEnabled('integrationsManagement') && !hasLookupConnector) || isFeatureEnabled('devManagement');
      default:
        return isFeatureEnabled(item.feature);
    }
  }
}

SettingsMenuBase.propTypes = {
  hasLookupConnector: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};

const SettingsMenu = connect(mapStateToProps)(SettingsMenuBase);
export { SettingsMenu };

function mapStateToProps({ getProvider }) {
  const lookupStatus = getProvider('externalCustomerLookupStatus').get();
  return {
    hasLookupConnector: lookupStatus.hasConnector,
  };
}

const AdminSettingsLink = () => {
  const route = '/admin';
  const handleClick = useCallback(evt => {
    evt.preventDefault();
    window.location.assign(route);
  }, []);
  return (
    <div className="navigationMenu-item">
      <StyledLink href={route} onClick={handleClick} className="navigationMenu-option">
        More Settings
      </StyledLink>
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/root/navigation_menu.jsx